import type { FC } from "react";

import { m } from "@recobra/i18n/messages";
import type { EditableTaskStatus, Task } from "@recobra/schemas/task";
import { DialogDescription, DialogHeader, DialogTitle } from "@recobra/ui/dialog";
import { Separator } from "@recobra/ui/separator";
import { Skeleton } from "@recobra/ui/skeleton";

import type { TaskProgressInput } from "../task-data";
import type { TaskCompletionInput } from "../task-execution";
import { type TaskExecutionState, taskContextRoute } from "../task-policy";
import { TaskModalContent } from "./TaskModalContent";

export const TaskModalPanel: FC<{
  task: Task | undefined;
  error: unknown;
  executionState: TaskExecutionState;
  executionOnly: boolean;
  contextRoute: ReturnType<typeof taskContextRoute>;
  isUpdating: boolean;
  onProgress: (taskId: string, input: TaskProgressInput) => void;
  onStatusChange: (taskId: string, status: EditableTaskStatus, waitingStatus?: string) => void;
  onConfirm: (taskId: string, input: TaskCompletionInput) => void;
  onClose: () => void;
}> = ({ task, error, ...props }) => {
  if (!task) return error ? <TaskModalUnavailable /> : <TaskModalLoading />;

  return (
    <TaskModalContent
      key={task.id}
      task={task}
      executionState={props.executionState}
      executionOnly={props.executionOnly}
      contextRoute={props.contextRoute}
      isUpdating={props.isUpdating}
      onProgress={(input) => props.onProgress(task.id, input)}
      onStatusChange={(status, waitingStatus) =>
        props.onStatusChange(task.id, status, waitingStatus)
      }
      onClose={props.onClose}
      onConfirm={(input) => props.onConfirm(task.id, input)}
    />
  );
};

const TaskModalLoading: FC = () => (
  <div className="flex flex-col gap-3 py-2">
    <Skeleton className="h-5 w-2/3" />
    <Skeleton className="h-4 w-1/2" />
    <Skeleton className="h-20 w-full" />
  </div>
);

const TaskModalUnavailable: FC = () => (
  <>
    <DialogHeader>
      <DialogTitle>{m.task_modal_unavailable_title()}</DialogTitle>
      <DialogDescription>{m.task_modal_unavailable_description()}</DialogDescription>
    </DialogHeader>
    <Separator />
  </>
);
